// 閲覧範囲（部署指定・シーズン指定・回答状況指定）の Excel 一括登録モーダル — 「部署指定拡張」PJ Ph.2・Ph.4（2026-10）・回答状況指定は 2026-10 追加パッチ
// ------------------------------------------------------------------
// 「閲覧範囲」画面（E3DepMgrPage）の「Excel一括登録」から開く。
//   ① ファイル選択（テンプレート .xlsx）→ 取り込み・行の検証（utils/e3DeptBulk.js）
//   ② サーバーで検証（POST /api/users/e3department/bulk-check/:groupCode。DB は変更しない）
//   ③ 結果表示（登録できる人・エラー一覧・エラー CSV）→ エラーがあれば「中止」/「エラーのある人を除いて登録」
//   ④ 確認モーダル（既存の指定は削除される旨。「戻る」= primary）→ 登録（POST …/bulk/:groupCode）
// 部署指定シート・シーズン指定シートは、シートに出てきた人のその項目だけを置き換える（Ph.4）。
import React, { useState } from 'react';
import { Modal, Button, Typography, Table, Alert, Space, Spin, message } from 'antd';
import { FileExcelOutlined, DownloadOutlined } from '@ant-design/icons';
import Dropzone from 'react-dropzone';
import Axios from 'axios';
import ExcelJS from 'exceljs';
import config from '../../Config/key';
import {
  ALL_COMPANIES,
  E3_BULK_SHEET,
  E3_BULK_SEASON_SHEET,
  E3_BULK_ANSWER_SHEET,
  ANSWER_VIEW,
  ANSWER_HIDE,
  ANSWER_KIND_COLUMNS,
  compareE3BulkErrors,
  parseE3DeptWorkbook,
  validateE3DeptHeaders,
  buildE3DeptItems,
  buildE3DeptErrorCsv,
} from '../../../utils/e3DeptBulk';
import { triggerBlobDownload, ymd8 } from '../../../utils/orgRosterHelper';

const { Text } = Typography;
const { confirm } = Modal;

const errorColumns = (affiliate) => [
  { title: 'シート', dataIndex: 'sheet', key: 'sheet', width: 104 },
  { title: '行', dataIndex: 'row', key: 'row', width: 48 },
  { title: 'メールアドレス', dataIndex: 'email', key: 'email', width: 170, ellipsis: true },
  ...(affiliate
    ? [{ title: '系列会社', dataIndex: 'company', key: 'company', width: 100, ellipsis: true }]
    : []),
  { title: '部署', dataIndex: 'department', key: 'department', width: 120, ellipsis: true },
  { title: 'シーズン', dataIndex: 'season', key: 'season', width: 140, ellipsis: true },
  { title: '回答状況指定', dataIndex: 'answerStatus', key: 'answerStatus', width: 160, ellipsis: true },
  { title: 'エラー内容', dataIndex: 'error', key: 'error', width: 280 },
];

// 件数の表示（0 件 = 制限なし）
const countText = (n) => (n === 0 ? '制限なし' : `${n} 件`);
const changeText = (current, next) =>
  next === undefined ? '変更なし' : `${countText(current || 0)} → ${countText(next)}`;

// 回答状況指定（閲覧可／閲覧不可）の表示。変わる種類だけ「360度系 閲覧可 → 閲覧不可」の形で並べる
const answerText = (canView) => (canView ? ANSWER_VIEW : ANSWER_HIDE);
const answerChangeText = (current, next) => {
  if (!next) return '変更なし';
  const changes = ANSWER_KIND_COLUMNS.filter((c) => (current || {})[c.kind] !== next[c.kind]).map(
    (c) => `${c.label} ${answerText((current || {})[c.kind] !== false)} → ${answerText(next[c.kind])}`
  );
  return changes.length > 0 ? changes.join('、') : '変更なし';
};

const okColumns = [
  { title: '氏名', dataIndex: 'name', key: 'name', width: 150, ellipsis: true },
  { title: 'メールアドレス', dataIndex: 'email', key: 'email', ellipsis: true },
  {
    title: '部署指定（現在 → 登録後）',
    key: 'dept',
    width: 190,
    render: (_, r) => changeText(r.current_count, r.new_count),
  },
  {
    title: 'シーズン指定（現在 → 登録後）',
    key: 'season',
    width: 190,
    render: (_, r) => changeText(r.current_season_count, r.new_season_count),
  },
  {
    title: '回答状況指定（現在 → 登録後）',
    key: 'answerStatus',
    width: 260,
    render: (_, r) => answerChangeText(r.current_answer_status_view, r.answer_status_view),
  },
];

// サーバーへ送る 1 人分（シートに出てきた項目だけ）
const toPayloadItem = (item) => {
  const payload = { email: item.email };
  if (item.e3_department !== undefined) payload.e3_department = item.e3_department;
  if (item.accessible_survey_seasons !== undefined) {
    payload.accessible_survey_seasons = item.accessible_survey_seasons;
  }
  if (item.answer_status_view !== undefined) {
    payload.answer_status_view = item.answer_status_view;
  }
  return payload;
};

/**
 * @param {{
 *   open: boolean,
 *   groupCode: string,
 *   model: ReturnType<import('../../../utils/e3DeptBulk').buildE3BulkModel>,
 *   onClose: () => void,
 *   onDone: () => void,   // 登録完了後（一覧の再読み込み用）
 * }} props
 */
function E3DeptBulkUploadModal({ open, groupCode, model, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  // 検証結果: { okItems, errors, excludedCount, usesAllCompanies }
  const [checked, setChecked] = useState(null);
  const affiliate = !!(model && model.affiliate);

  const reset = () => {
    setFileName('');
    setFileError('');
    setChecked(null);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles && acceptedFiles[0];
    if (!file) return;
    reset();
    setFileName(file.name);
    setBusy(true);
    try {
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(await file.arrayBuffer());
      const parsed = parseE3DeptWorkbook(workbook);
      const headerError = validateE3DeptHeaders(parsed, model);
      if (headerError) {
        setFileError(headerError);
        return;
      }
      if (parsed.rows.length === 0 && parsed.seasonRows.length === 0 && parsed.answerRows.length === 0) {
        setFileError(
          `登録する行がありません（「${E3_BULK_SHEET}」「${E3_BULK_SEASON_SHEET}」「${E3_BULK_ANSWER_SHEET}」のいずれかのシートの 5 行目以降に記入してください）。`
        );
        return;
      }
      const { items, rowErrors, excludedEmails } = buildE3DeptItems(parsed.rows, model, {
        seasonRows: parsed.seasonRows,
        seasonMap: parsed.seasonMap,
        answerRows: parsed.answerRows,
      });
      const okItems = [];
      const errors = [...rowErrors];
      let excludedCount = excludedEmails.length;
      if (items.length > 0) {
        const res = await Axios.post(
          `${config.USER_SERVER}/e3department/bulk-check/${groupCode}`,
          { items: items.map(toPayloadItem) }
        );
        const results = res.data.results || [];
        results.forEach((result, idx) => {
          const item = items[idx];
          if (result.ok) {
            okItems.push({ ...result, ...toPayloadItem(item), usesAllCompanies: item.usesAllCompanies });
          } else {
            excludedCount += 1;
            errors.push({
              sheet:
                item.e3_department !== undefined
                  ? E3_BULK_SHEET
                  : item.accessible_survey_seasons !== undefined
                  ? E3_BULK_SEASON_SHEET
                  : E3_BULK_ANSWER_SHEET,
              row: item.rows[0],
              email: item.email,
              company: '',
              department: '',
              season: '',
              answerStatus: '',
              error: item.rows.length > 1
                ? `${result.error}（${item.rows.length} 行すべて登録されません）`
                : result.error,
            });
          }
        });
      }
      errors.sort(compareE3BulkErrors);
      setChecked({
        okItems,
        errors,
        excludedCount,
        usesAllCompanies: okItems.some((i) => i.usesAllCompanies),
      });
    } catch (error) {
      const serverError = error && error.response && error.response.data && error.response.data.error;
      setFileError(
        serverError
          ? `ファイルを確認できませんでした。${serverError}`
          : 'ファイルを読み込めませんでした。画面からダウンロードしたテンプレート（.xlsx）を使用してください。'
      );
      console.log(error);
    } finally {
      setBusy(false);
    }
  };

  const downloadErrors = () => {
    triggerBlobDownload(
      buildE3DeptErrorCsv(checked.errors),
      `閲覧範囲_エラー一覧_${groupCode}_${ymd8()}.csv`,
      'text/csv;charset=utf-8'
    );
  };

  const apply = async () => {
    setBusy(true);
    try {
      const res = await Axios.post(`${config.USER_SERVER}/e3department/bulk/${groupCode}`, {
        items: checked.okItems.map(toPayloadItem),
        skipped_count: checked.excludedCount,
      });
      if (res.data.success) {
        message.success(
          `${res.data.updated} 名の閲覧範囲を登録しました。` +
            (checked.usesAllCompanies
              ? `（「${ALL_COMPANIES}」は登録時点の系列会社を個別に登録しました）`
              : '')
        );
        reset();
        onDone();
      }
    } catch (error) {
      const serverError = error && error.response && error.response.data && error.response.data.error;
      message.error(serverError ? `登録に失敗しました。${serverError}` : '登録に失敗しました。');
      console.log(error);
    } finally {
      setBusy(false);
    }
  };

  // 登録前の確認（安全側の「戻る」を primary にする）
  const confirmApply = () => {
    const deptCount = checked.okItems.filter((i) => i.e3_department !== undefined).length;
    const seasonCount = checked.okItems.filter((i) => i.accessible_survey_seasons !== undefined).length;
    const answerCount = checked.okItems.filter((i) => i.answer_status_view !== undefined).length;
    confirm({
      title: '既存の指定は削除されます',
      width: 560,
      content: (
        <div>
          {deptCount > 0 && (
            <p>部署指定: 対象 {deptCount} 名の既存の部署指定はすべて削除され、ファイルの内容に置き換わります。</p>
          )}
          {seasonCount > 0 && (
            <p>シーズン指定: 対象 {seasonCount} 名の既存のシーズン指定はすべて削除され、ファイルの内容に置き換わります。</p>
          )}
          {answerCount > 0 && (
            <p>回答状況指定: 対象 {answerCount} 名の回答状況指定のうち、ファイルに記入した種類が置き換わります。</p>
          )}
          {checked.excludedCount > 0 && (
            <p>エラーのある {checked.excludedCount} 名は登録されません（現在の指定のままです）。</p>
          )}
          {checked.usesAllCompanies && (
            <p>
              「{ALL_COMPANIES}」は、現在の系列会社（{model.companies.length} 社）をそれぞれ登録します。
              今後追加される系列会社は自動では追加されません。
            </p>
          )}
          <p>よろしいですか？</p>
        </div>
      ),
      okText: '登録する',
      cancelText: '戻る',
      okButtonProps: { type: 'default' },
      cancelButtonProps: { type: 'primary' },
      onOk: apply,
    });
  };

  const hasErrors = checked && checked.errors.length > 0;
  const okCount = checked ? checked.okItems.length : 0;
  const footer = !checked
    ? [<Button key="close" onClick={handleClose} disabled={busy}>閉じる</Button>]
    : [
        <Button key="reselect" onClick={reset} disabled={busy}>別のファイルを選ぶ</Button>,
        <Button key="cancel" onClick={handleClose} disabled={busy}>
          {hasErrors ? '中止' : 'キャンセル'}
        </Button>,
        <Button key="ok" type="primary" onClick={confirmApply} disabled={okCount === 0} loading={busy}>
          {hasErrors ? 'エラーのある人を除いて登録' : '登録する'}
        </Button>,
      ];

  return (
    <Modal
      title="閲覧範囲（部署指定・シーズン指定・回答状況指定） Excel一括登録"
      open={open}
      onCancel={handleClose}
      maskClosable={false}
      destroyOnHidden
      width={1040}
      footer={footer}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        <Alert
          type="warning"
          showIcon
          message="シートに記入した人の既存の指定（部署指定シートなら部署、シーズン指定シートならシーズン）はすべて削除され、ファイルの内容に置き換わります。"
          description="ファイルに記入していない人、記入していないシートの項目は変更されません。対象はシーズン管理者（role 7）のみです。"
        />
        {!checked && (
          <Spin spinning={busy} tip="確認しています…">
            <Dropzone onDrop={onDrop} multiple={false} accept=".xlsx" disabled={busy}>
              {({ getRootProps, getInputProps }) => (
                <div
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    minHeight: '160px',
                    border: '1px dashed var(--grey-30)',
                    borderRadius: 'var(--radius-lg)',
                    backgroundColor: 'var(--grey-10)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-3)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: busy ? 'not-allowed' : 'pointer',
                    padding: 'var(--space-6)',
                    textAlign: 'center',
                  }}
                  {...getRootProps()}
                >
                  <input {...getInputProps()} />
                  <FileExcelOutlined style={{ fontSize: 'var(--icon-2xl)', color: 'var(--grey-45)' }} />
                  <Text type="secondary">
                    閲覧範囲のテンプレート（.xlsx）をドラッグ＆ドロップ、またはクリックして選択してください。
                  </Text>
                </div>
              )}
            </Dropzone>
          </Spin>
        )}
        {fileError && <Alert type="error" showIcon message={fileError} />}
        {checked && (
          <>
            <Text type="secondary">ファイル: {fileName}</Text>
            <Alert
              type={okCount > 0 ? (hasErrors ? 'warning' : 'success') : 'error'}
              showIcon
              message={
                okCount > 0
                  ? `${okCount} 名を登録できます。` +
                    (hasErrors ? `エラーが ${checked.errors.length} 件あり、${checked.excludedCount} 名は登録されません。` : '')
                  : `登録できる人がいません（エラー ${checked.errors.length} 件）。`
              }
              description={
                hasErrors
                  ? 'エラーのある行を含む人は、その人の行すべて（両シートとも）が登録されません（指定を丸ごと置き換えるため）。'
                  : undefined
              }
            />
            {hasErrors && (
              <>
                <Space>
                  <Text strong>エラー一覧</Text>
                  <Button size="small" icon={<DownloadOutlined />} onClick={downloadErrors}>
                    エラー一覧CSV
                  </Button>
                </Space>
                <Table
                  size="small"
                  rowKey={(r) => `${r.sheet}-${r.row}-${r.email}-${r.error}`}
                  columns={errorColumns(affiliate)}
                  dataSource={checked.errors}
                  scroll={{ x: 'max-content' }}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                />
              </>
            )}
            {okCount > 0 && (
              <>
                <Text strong>登録する人</Text>
                <Table
                  size="small"
                  rowKey="email"
                  columns={okColumns}
                  dataSource={checked.okItems}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                />
              </>
            )}
          </>
        )}
      </Space>
    </Modal>
  );
}

export default E3DeptBulkUploadModal;
